import { Box, Theme, Typography } from '@mui/material';
import { useSelector } from 'react-redux';

import { CheckmarkIcon } from '@/icons';
import { EVENT_CONSTANTS } from '@/services';
import { selectUserCompliance } from '@/store';
import { Referrer } from '@/types';

import { RegisterForm } from '../Auth';
import { StylizedUnderline } from './components';

export const FreeTrialPrompt = ({ referrer }: { referrer?: Referrer }) => {
  const compliance = useSelector(selectUserCompliance);

  const benefits = [
    'Finance focused content your team needs to excel',
    'Engaging episodes and enjoyable experience',
    `The most convenient way to earn ${compliance}`,
  ];

  return (
    <Box sx={styles.root}>
      <Box sx={styles.background} />
      <Box sx={styles.contentContainer}>
        <Box sx={styles.textContainer}>
          <Box sx={styles.titleContainer}>
            <Typography variant="hero3">
              <span style={{ display: 'inline-block' }}>Start your&nbsp;</span>
              <span style={{ display: 'inline-block', position: 'relative' }}>
                free trial!
                <StylizedUnderline sx={styles.underline} />
              </span>
            </Typography>
          </Box>
          <Typography variant="h3" color="text.main">
            Hear the LumiQ difference for yourself.
          </Typography>
          <Box sx={styles.benefitList}>
            {benefits.map((benefit, index) => (
              <Box key={index} sx={styles.benefitItem}>
                <CheckmarkIcon sx={styles.checkboxIcon} />
                <Typography variant="body2">{benefit}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={styles.registerFormContainer}>
          <RegisterForm
            subtitle="Start your free trial! No credit card required."
            stretch
            referrer={{
              ...referrer,
              component: EVENT_CONSTANTS.COMPONENT.INLINE_SIGNUP_FORM,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  root: (theme: Theme) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    py: {
      xs: 6,
      md: 3,
    },
    // Note: component has negative margins to allow it to spread over the default page margins
    mx: {
      xs: -theme.dimensions.appLayout.px.xs,
      md: 0,
    },
    px: {
      xs: theme.dimensions.appLayout.px.xs,
      md: 0,
    },
    background: {
      xs: theme.palette.gradients.secondary,
      md: 'none',
    },
  }),
  background: {
    display: {
      xs: 'none',
      md: 'block',
    },
    position: 'absolute',
    height: 430,
    width: '100%',
    background: (theme: Theme) => theme.palette.gradients.secondary,
    zIndex: -1,
  },
  contentContainer: {
    display: 'flex',
    width: '100%',
    flex: 1,
    maxWidth: 1200,
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 4,
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  titleContainer: {
    width: 'fit-content',
    position: 'relative',
  },
  underline: {
    position: 'absolute',
    left: 0,
    bottom: -10,
    width: {
      xs: 150,
      md: 200,
    },
    ml: 'auto',
  },
  benefitList: { display: 'flex', flexDirection: 'column', gap: 1.5 },
  benefitItem: { display: 'flex', alignItems: 'center', gap: 1.5 },
  checkboxIcon: {
    color: 'white',
    width: 24,
    height: 24,
    padding: 0.5,
    borderRadius: 3,
    background: (theme: Theme) => theme.palette.gradients.primary,
  },
  registerFormContainer: { maxWidth: 540, flex: 1, width: '100%' },
};
